import * as THREE from 'three';
import { detectBoundary, getBoundaryPositions, computeBoundaryCentroid, computeBoundaryCurveNormals } from './boundaryDetection';
import {
  mapVerticesToSegments,
  extendSegmentMapping,
  createVertexBoundaryYMap,
  mapBoundaryVerticesByArcLength,
} from './segmentMapping';
import {
  computeDistanceFromBoundary,
  computeGeodesicDistanceAndSegment,
} from './distanceField';
import { applyFlareWithDistanceFalloff } from './flareTransforms';
import { buildAdjacency, smoothAllSegmentBoundaries, identifyAngularBoundaryVertices, smoothAngularBoundaries, smoothFlareTransitionZone, smoothBoundaryCurve, detectMeshBoundaryVertices } from './flareSmoothingHelpers';
import { checkGeometryHealth } from './geometryValidation';
import { repairGeometry } from './geometryRepair';
import { getAngularlyBlendedParameters } from './flareParameterBlending';

const MM_TO_UNIT = 0.1; // 1 mm = 0.1 cm (scene units)

/**
 * Majority-vote smoothing of segment labels in the flare band removes salt-and-pepper
 * from discrete multi-source geodesic Voronoi without moving trimline seed labels.
 */
function smoothGeodesicSegmentIdsInBand(
  geometry,
  segmentIds,
  distances,
  maxFlareInfluenceDistance,
  boundaryVertexSet,
  iterations = 5
) {
  let adjacency = geometry.userData.flareAdjacency;
  if (!adjacency) {
    adjacency = buildAdjacency(geometry);
    geometry.userData.flareAdjacency = adjacency;
  }

  const vcount = segmentIds.length;
  let current = segmentIds;

  for (let iter = 0; iter < iterations; iter++) {
    const next = new Int32Array(current);

    for (let i = 0; i < vcount; i++) {
      if (boundaryVertexSet.has(i)) continue;
      if (!isFinite(distances[i]) || distances[i] > maxFlareInfluenceDistance) continue;
      if (current[i] < 0) continue;

      const nbrs = adjacency[i];
      if (!nbrs || nbrs.length < 2) continue;

      const counts = new Map();
      for (let k = 0; k < nbrs.length; k++) {
        const j = nbrs[k];
        if (!isFinite(distances[j]) || distances[j] > maxFlareInfluenceDistance) continue;
        const sj = current[j];
        if (sj < 0) continue;
        counts.set(sj, (counts.get(sj) || 0) + 1);
      }

      let total = 0;
      for (const c of counts.values()) total += c;
      if (total < 2) continue;

      let bestSeg = current[i];
      let bestCnt = -1;
      for (const [seg, cnt] of counts) {
        if (cnt > bestCnt || (cnt === bestCnt && seg < bestSeg)) {
          bestCnt = cnt;
          bestSeg = seg;
        }
      }

      if (bestCnt * 2 > total) {
        next[i] = bestSeg;
      }
    }
    current = next;
  }

  return current;
}

function flareResolutionEmpty(geometry) {
  const vc = geometry?.attributes?.position?.count ?? 0;
  return {
    distances: new Float32Array(vc).fill(Infinity),
    extendedSegmentMap: new Map(),
    boundarySegmentMap: new Map(),
    boundaryVertices: [],
    boundaryPositions: [],
    centroid: new THREE.Vector3(),
    centroidLocal: new THREE.Vector3(),
    boundaryYMap: new Map(),
    maxFlareInfluenceDistance: 0,
  };
}

/**
 * Resolve geodesic (multi-source) or angular segment assignment + distance field.
 * Used by applyFlareToGeometry and preview so both stay in sync.
 *
 * @returns {Object} distances, extendedSegmentMap, boundarySegmentMap, boundaryVertices, boundaryPositions, centroid, centroidLocal, boundaryYMap, maxFlareInfluenceDistance
 */
export function resolveFlareDistanceAndSegmentMapping(geometry, originalMesh, flareState, trimlineState, options = {}) {
  if (!geometry || !originalMesh) return flareResolutionEmpty(geometry);

  const boundaryVertices = detectBoundary(originalMesh, trimlineState);
  if (boundaryVertices.length === 0) return flareResolutionEmpty(geometry);

  const boundaryPositions = getBoundaryPositions(originalMesh, boundaryVertices);
  const centroid = computeBoundaryCentroid(boundaryPositions);
  const worldToLocal = new THREE.Matrix4().copy(originalMesh.matrixWorld).invert();
  const centroidLocal = centroid.clone().applyMatrix4(worldToLocal);

  const boundaryYMap = new Map();
  for (let i = 0; i < boundaryVertices.length; i++) {
    boundaryYMap.set(boundaryVertices[i], boundaryPositions[i].y);
  }

  const totalHeights = flareState.segmentHeightValues.map((h, i) => {
    const falloffH = flareState.segmentFalloffHeightValues?.[i] || 0;
    return h + falloffH;
  });
  const maxTotalHeightMm = Math.max(...totalHeights, 0);
  const maxFlareInfluenceDistance = maxTotalHeightMm * MM_TO_UNIT;
  const requiredPasses = Math.min(100, Math.max(15, Math.ceil(maxTotalHeightMm * 3)));
  // Multi-source labels need more Jacobi iterations than single-source distance; cap for perf.
  const geodesicJacobiPasses = Math.min(320, Math.max(96, requiredPasses * 4));

  // Only the flare influence band matters downstream; bounding the geodesic sweep to it (with a
  // small safety margin) keeps the active vertex set small. This is the main fix for the
  // multi-second freeze when entering the flares step.
  const pruneDistance = maxFlareInfluenceDistance > 0
    ? maxFlareInfluenceDistance * 1.2
    : Infinity;
  const geodesicOptions = { maxDistance: pruneDistance };

  const segmentMode = flareState.segmentAssignmentMode ?? 'geodesic';

  if (segmentMode === 'geodesic') {
    const arcMap = mapBoundaryVerticesByArcLength(
      boundaryVertices,
      boundaryPositions,
      flareState.flareValues
    );
    if (arcMap && arcMap.size > 0) {
      const { distances, segmentIds: rawSegmentIds } = computeGeodesicDistanceAndSegment(
        geometry,
        arcMap,
        geodesicJacobiPasses,
        geodesicOptions
      );
      const boundarySeedSet = new Set(arcMap.keys());
      const segmentIds = smoothGeodesicSegmentIdsInBand(
        geometry,
        rawSegmentIds,
        distances,
        maxFlareInfluenceDistance,
        boundarySeedSet,
        5
      );
      const extendedSegmentMap = new Map();
      const vc = distances.length;
      for (let i = 0; i < vc; i++) {
        if (!isFinite(distances[i]) || distances[i] > maxFlareInfluenceDistance) continue;
        if (segmentIds[i] < 0) continue;
        extendedSegmentMap.set(i, segmentIds[i]);
      }
      for (const [bv, seg] of arcMap) {
        extendedSegmentMap.set(bv, seg);
      }
      return {
        distances,
        extendedSegmentMap,
        boundarySegmentMap: arcMap,
        boundaryVertices,
        boundaryPositions,
        centroid,
        centroidLocal,
        boundaryYMap,
        maxFlareInfluenceDistance,
      };
    }
  }

  const boundarySegmentMap = mapVerticesToSegments(
    boundaryVertices,
    boundaryPositions,
    centroid,
    flareState.flareValues
  );
  const distances = computeDistanceFromBoundary(geometry, boundaryVertices, requiredPasses, geodesicOptions);
  const extendedSegmentMap = extendSegmentMapping(
    geometry,
    boundarySegmentMap,
    distances,
    maxFlareInfluenceDistance,
    centroidLocal,
    flareState.flareValues
  );

  return {
    distances,
    extendedSegmentMap,
    boundarySegmentMap,
    boundaryVertices,
    boundaryPositions,
    centroid,
    centroidLocal,
    boundaryYMap,
    maxFlareInfluenceDistance,
  };
}

/**
 * Distance field for flare preview overlays — matches applyFlareToGeometry resolution.
 */
export function computeFlareDistanceFieldForPreview(geometry, originalMesh, flareState, trimlineState) {
  return resolveFlareDistanceAndSegmentMapping(geometry, originalMesh, flareState, trimlineState).distances;
}

// Segment color mapping (matches FlareToolCard.jsx colors)
const SEGMENT_COLORS = {
  purple: new THREE.Color('#B984D9'),
  yellow: new THREE.Color('#FFD152'),
  orange: new THREE.Color('#F19B4A'),
  blue: new THREE.Color('#3B9FF5'),
  violet: new THREE.Color('#9093DF')
};

/**
 * Main function to apply flare effect to geometry
 * @param {THREE.BufferGeometry} geometry - Target geometry (will be modified)
 * @param {THREE.Mesh} originalMesh - Original mesh for world transforms
 * @param {Object} flareState - Flare state from useFlareStore
 * @param {Object} trimlineState - Trimline state from useTrimlineStore
 * @param {Object|null} cachedResolve - Optional result of resolveFlareDistanceAndSegmentMapping (avoids duplicate work in preview)
 * @param {Object} [options] - { quality: 'preview' | 'full' }. 'preview' (default) does the
 *   deflection + a single light boundary-smoothing pass for a real-time preview, skipping the
 *   expensive multi-pass smoothing and geometry repair. 'full' runs the complete pipeline and is
 *   meant for the Apply step (can take time).
 * @returns {Map} Extended segment mapping (vertex index -> segment index)
 */
export function applyFlareToGeometry(geometry, originalMesh, flareState, trimlineState, cachedResolve = null, options = {}) {
  if (!geometry || !originalMesh) return new Map();

  const quality = options.quality === 'full' ? 'full' : 'preview';
  const isPreview = quality === 'preview';

  const resolved =
    cachedResolve ??
    resolveFlareDistanceAndSegmentMapping(geometry, originalMesh, flareState, trimlineState, { quality });
  const {
    distances,
    extendedSegmentMap,
    boundarySegmentMap,
    boundaryVertices,
    boundaryPositions,
    centroid,
    centroidLocal,
    boundaryYMap,
    maxFlareInfluenceDistance: maxHeight,
  } = resolved;

  if (boundaryVertices.length === 0) {
    console.warn('No boundary vertices found for flare application');
    return new Map();
  }

  // Create vertex to boundary map (for local boundary reference and deflection)
  // This allows each vertex to use the Y of its nearest boundary vertex
  // and inherit its deflection direction
  // Pass extendedSegmentMap to ensure we only match within the same segment
  const vertexBoundaryMap = createVertexBoundaryYMap(
    geometry,
    boundarySegmentMap,
    boundaryYMap,
    distances,
    maxHeight,
    extendedSegmentMap
  );

  // Compute boundary deflection directions (smoothed surface normals)
  // This ensures flares extend outward without overlapping in concave regions
  const boundaryCurveNormals = computeBoundaryCurveNormals(
    boundaryVertices,
    boundaryPositions,
    originalMesh,
    centroid
  );

  // Ensure geometry has normals
  if (!geometry.attributes.normal) {
    geometry.computeVertexNormals();
  }

  const positions = geometry.attributes.position;
  const normals = geometry.attributes.normal;
  const posArray = positions.array;

  // Create inverse transform matrix to convert world-space directions to local space
  const worldToLocal = new THREE.Matrix4().copy(originalMesh.matrixWorld).invert();

  // Apply flare transformations
  for (let i = 0; i < positions.count; i++) {
    const segmentIndex = extendedSegmentMap.get(i);
    if (segmentIndex === undefined) continue;

    // Get local boundary info for this vertex
    const boundaryInfo = vertexBoundaryMap.get(i);
    if (!boundaryInfo) continue;

    const { boundaryY, boundaryIndex } = boundaryInfo;

    // Get deflection direction from boundary curve normal (in world space)
    const deflectionDirectionWorld = boundaryCurveNormals.get(boundaryIndex);
    if (!deflectionDirectionWorld) continue;

    // Transform deflection direction from world space to local space.
    // Use transformDirection to correctly handle rotation/scale without translation.
    const deflectionDirection = deflectionDirectionWorld.clone().transformDirection(worldToLocal).normalize();

    // Get original position (in local space)
    const originalPos = new THREE.Vector3(
      posArray[i * 3],
      posArray[i * 3 + 1],
      posArray[i * 3 + 2]
    );

    // Get angularly blended parameters for smooth transitions between segments
    const blendedParams = getAngularlyBlendedParameters(
      originalPos,
      segmentIndex,
      centroidLocal,
      flareState.flareValues,
      flareState
    );

    // Use geodesic distance from boundary for stable falloff on curved regions
    const heightMm = blendedParams.heightMm;
    const deflectMm = blendedParams.deflectMm;
    const falloffHeightMm = blendedParams.falloffHeightMm;
    const falloffDeflectMm = blendedParams.falloffDeflectMm;
    const totalHeightMm = heightMm + falloffHeightMm;
    const distanceFromBoundary = distances[i];

    if (!isFinite(distanceFromBoundary)) continue;

    // Special handling for zero-height segments:
    // Allow boundary vertices (distance ≈ 0) to be processed for deflection
    // even when segment has zero vertical extent
    const MIN_BOUNDARY_DISTANCE = 0.002; // ~0.02mm threshold for boundary vertices
    const effectiveMaxDistance = totalHeightMm > 0
      ? totalHeightMm * MM_TO_UNIT
      : MIN_BOUNDARY_DISTANCE;

    if (distanceFromBoundary > effectiveMaxDistance) continue;

    // Apply flare transformation using distance-based falloff to avoid artifacts
    // Now both originalPos and deflectionDirection are in local space
    // Pass blended parameters as overrides to ensure smooth angular transitions
    const newPos = applyFlareWithDistanceFalloff(
      originalPos,
      distanceFromBoundary,
      segmentIndex,
      deflectionDirection,
      centroid,
      {
        segmentHeightValues: flareState.segmentHeightValues,
        segmentDeflectValues: flareState.segmentDeflectValues,
        segmentFalloffHeightValues: flareState.segmentFalloffHeightValues,
        segmentFalloffDeflectValues: flareState.segmentFalloffDeflectValues,
        deflectBlendRatio: flareState.deflectBlendRatio
      },
      blendedParams  // Pass blended parameters as override
    );

    // Update position
    positions.setXYZ(i, newPos.x, newPos.y, newPos.z);
  }

  // Mark geometry as needing update
  positions.needsUpdate = true;

  // Detect mesh boundary vertices (edges belonging to only one triangle)
  // These must be preserved to maintain clean mesh edges. Topology is constant across preview
  // rebuilds (only positions change), so cache the result to avoid recomputing every rebuild.
  let meshBoundaryVertices = geometry.userData.flareMeshBoundary;
  if (!meshBoundaryVertices) {
    meshBoundaryVertices = detectMeshBoundaryVertices(geometry);
    geometry.userData.flareMeshBoundary = meshBoundaryVertices;
  }

  // Smooth boundary curve to ensure continuity across segment transitions
  // Build or reuse cached adjacency graph
  let adjacency = geometry.userData.flareAdjacency;
  if (!adjacency) {
    adjacency = buildAdjacency(geometry);
    // Cache adjacency in geometry userData for reuse across rebuilds
    geometry.userData.flareAdjacency = adjacency;
  }

  // Apply boundary curve smoothing first to fix breaks at segment transitions
  // Pass meshBoundaryVertices to preserve actual mesh edges.
  // Preview uses fewer iterations for a real-time "light smoothing" pass.
  smoothBoundaryCurve(
    geometry,
    boundaryVertices,
    extendedSegmentMap,
    flareState.flareValues,
    centroidLocal,
    adjacency,
    {
      iterations: isPreview ? 2 : 4,
      strength: isPreview ? 0.6 : 0.8,
      blendZoneDegrees: 15 // Smooth within 15° of segment boundaries
    },
    meshBoundaryVertices // Pass to preserve mesh boundaries
  );

  // Preview stops here: the deflection + single light boundary pass is enough for a
  // real-time preview. The expensive multi-pass smoothing and geometry repair below run
  // only for the full-quality Apply pass.
  if (isPreview) {
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();

    geometry.userData.segmentMap = extendedSegmentMap;
    geometry.userData.centroid = centroidLocal;
    geometry.userData.flareValues = flareState.flareValues;

    return extendedSegmentMap;
  }

  // Apply boundary smoothing to eliminate sharp edges at segment bottoms
  // Smooth segment boundaries (bottom edges)
  smoothAllSegmentBoundaries(geometry, extendedSegmentMap, flareState, adjacency, null, meshBoundaryVertices);

  // Apply angular boundary smoothing to eliminate sharp edges between segments
  const smoothingConfig = flareState.smoothingConfig || {};
  if (smoothingConfig.enabled && smoothingConfig.enableAngularSmoothing !== false) {
    const blendZoneDegrees = smoothingConfig.angularBlendZoneDegrees || 5;
    const angularIterations = smoothingConfig.angularSmoothingIterations || 4;
    const angularStrength = smoothingConfig.angularSmoothingStrength || 0.6;

    // Identify vertices near angular boundaries (avoid very top/bottom strip)
    const angularBoundaryData = identifyAngularBoundaryVertices(
      geometry,
      extendedSegmentMap,
      flareState.flareValues,
      centroidLocal, // Angular checks are done in local space
      blendZoneDegrees,
      {
        distances,
        segmentHeightsMm: flareState.segmentHeightValues,
        distanceBand: [0.15, 0.85] // only smooth between 15% and 85% of height
      }
    );

    // Apply smoothing to angular boundaries
    if (angularBoundaryData.size > 0) {
      smoothAngularBoundaries(
        geometry,
        angularBoundaryData,
        adjacency,
        angularIterations,
        angularStrength,
        meshBoundaryVertices
      );
    }
  }

  // Apply transition zone smoothing to prevent sharp normal discontinuities
  // This smooths the boundary between flared and non-flared regions
  if (smoothingConfig.enabled && smoothingConfig.enableTransitionSmoothing !== false) {
    const transitionIterations = smoothingConfig.transitionSmoothingIterations || 5;
    const transitionStrength = smoothingConfig.transitionSmoothingStrength || 0.7;

    smoothFlareTransitionZone(
      geometry,
      extendedSegmentMap,
      distances,
      flareState.segmentHeightValues,
      flareState.segmentFalloffHeightValues,
      adjacency,
      {
        iterations: transitionIterations,
        strength: transitionStrength
      },
      meshBoundaryVertices
    );
  }

  // Repair geometry to fix degenerate triangles without creating holes
  // This merges close vertices and collapses short edges instead of removing triangles
  // Pass mesh boundary vertices to prevent removing faces at boundaries
  repairGeometry(geometry, {
    mergeVertices: true,    // Merge vertices that are extremely close
    collapseEdges: true,    // Collapse very short edges
    smoothDistorted: true,  // Smooth areas with geometric distortion
    verbose: true,
    meshBoundaryVertices: meshBoundaryVertices  // Preserve mesh boundaries
  });

  // Recompute normals and bounds after repair
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();

  // NEW: Store the segment map in geometry for direct segment-based coloring
  // This ensures the overlay visualization uses the SAME segmentation as the geometry modification
  geometry.userData.segmentMap = extendedSegmentMap;
  geometry.userData.centroid = centroidLocal;
  geometry.userData.flareValues = flareState.flareValues;

  // Optional: Check geometry health for debugging
  // Uncomment to see geometry health report
  // const health = checkGeometryHealth(geometry);
  // console.log('[flareHelpers] Geometry health:', health);

  // Return segment mapping for overlay visualization
  return extendedSegmentMap;
}

/**
 * Create segment color attribute for direct segment-based visualization
 * NEW: This creates a single colored overlay mesh based on actual segment assignments
 * @param {THREE.BufferGeometry} geometry - Target geometry
 * @param {Map} segmentMap - Vertex index -> segment index mapping
 * @param {Array} segmentNames - Segment names ['purple', 'yellow', etc.]
 * @returns {THREE.BufferAttribute} Color attribute for visualization
 */
export function createSegmentColorAttribute(geometry, segmentMap, segmentNames) {
  const vertexCount = geometry.attributes.position.count;
  const colors = new Float32Array(vertexCount * 3);
  const defaultColor = new THREE.Color(0x808080); // Gray for unmapped vertices

  for (let i = 0; i < vertexCount; i++) {
    const segmentIndex = segmentMap.get(i);
    let color = defaultColor;

    if (segmentIndex !== undefined && segmentIndex >= 0 && segmentIndex < segmentNames.length) {
      const segmentName = segmentNames[segmentIndex];
      color = SEGMENT_COLORS[segmentName] || defaultColor;
    }

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  return new THREE.BufferAttribute(colors, 3);
}

/**
 * Add segment colors to geometry for visual feedback
 * @param {THREE.BufferGeometry} geometry - Target geometry
 * @param {Map} segmentMap - Vertex index -> segment index mapping
 * @param {Array} segmentNames - Segment names ['purple', 'yellow', etc.]
 * @param {number} selectedSegmentIndex - Currently selected segment (optional, for highlighting)
 */
export function addSegmentColorsToGeometry(geometry, segmentMap, segmentNames, selectedSegmentIndex = -1) {
  const positions = geometry.attributes.position;
  const vertexCount = positions.count;

  // Create color array
  const colors = new Float32Array(vertexCount * 3);
  const defaultColor = new THREE.Color(0x808080); // Gray for unmapped vertices

  for (let i = 0; i < vertexCount; i++) {
    const segmentIndex = segmentMap.get(i);
    let color = defaultColor;

    if (segmentIndex !== undefined && segmentIndex >= 0 && segmentIndex < segmentNames.length) {
      const segmentName = segmentNames[segmentIndex];
      color = SEGMENT_COLORS[segmentName] || defaultColor;

      // Optional: Brighten selected segment for better visual feedback
      if (segmentIndex === selectedSegmentIndex) {
        color = color.clone().multiplyScalar(1.3); // 30% brighter
      }
    }

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  // Add or update color attribute
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  return geometry;
}

/**
 * Check if any flare is applied (any segment has non-zero height/deflect/falloff)
 * @param {Object} flareState - Flare state from useFlareStore
 * @returns {boolean} True if any flare is applied
 */
export function hasFlareApplied(flareState) {
  return flareState.segmentHeightValues.some(h => h > 0) ||
         flareState.segmentDeflectValues.some(d => d > 0) ||
         (flareState.segmentFalloffHeightValues && flareState.segmentFalloffHeightValues.some(h => h > 0)) ||
         (flareState.segmentFalloffDeflectValues && flareState.segmentFalloffDeflectValues.some(d => d > 0));
}
